/**
 * Stock Movement Modal Component
 *
 * Modal for recording stock movements (entry, exit, adjustment)
 * Supports both FIFO automatic and Manual batch selection for exits
 *
 * @module components/inventory/StockMovementModal
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Button, Form, Alert, Badge, Spinner, Card, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { toast } from '../../utils/toast';
import {
  recordStockMovement,
  previewFIFOConsumption,
  consumeInventoryFIFO,
  consumeFromSpecificBatches
} from '../../services/inventoryService';
import { listProjects } from '../../services/projectService';
import { listTasks } from '../../services/taskService';
import { MOVEMENT_TYPE, MOVEMENT_REASON, MOVEMENT_REASON_LABELS, PROJECT_STATUS } from '../../utils/constants';
import { formatCurrency } from '../../utils/formatters';
import BatchSelector from './BatchSelector';
import { useAuth } from '../../contexts/AuthContext';

const SELECTION_MODE = {
  FIFO: 'fifo',
  MANUAL: 'manual',
};

const StockMovementModal = ({ show, onHide, inventoryItem, onSuccess }) => {
  const { isAdmin } = useAuth();
  const [movementType, setMovementType] = useState(MOVEMENT_TYPE.ENTRY);
  const [movementReason, setMovementReason] = useState(MOVEMENT_REASON.PROJECT_CONSUMPTION);
  const [quantity, setQuantity] = useState('');
  const [projectId, setProjectId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fifoPreview, setFifoPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectionMode, setSelectionMode] = useState(SELECTION_MODE.FIFO);
  const [batchSelection, setBatchSelection] = useState(null);
  const [justification, setJustification] = useState('');

  // Whether to show batch selection mode toggle
  const showBatchToggle = movementType === MOVEMENT_TYPE.EXIT && inventoryItem?.track_batches !== false;

  // Available reasons based on movement type
  const availableReasons = useMemo(() => {
    if (movementType === MOVEMENT_TYPE.ENTRY) {
      return [
        MOVEMENT_REASON.PURCHASE,
        MOVEMENT_REASON.INITIAL_STOCK,
        MOVEMENT_REASON.INVENTORY_ADJUSTMENT,
      ];
    } else if (movementType === MOVEMENT_TYPE.EXIT) {
      return [
        MOVEMENT_REASON.PROJECT_CONSUMPTION,
        MOVEMENT_REASON.LOSS_DAMAGE,
        MOVEMENT_REASON.SUPPLIER_RETURN,
        MOVEMENT_REASON.TRANSFER,
      ];
    } else {
      return [MOVEMENT_REASON.INVENTORY_ADJUSTMENT];
    }
  }, [movementType]);

  // Fetch active projects for Exit movements
  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-exit'],
    // Issue #296: 'active' is a pre-014 project status value that no longer
    // exists in the live CHECK constraint -- the real in-execution status is
    // now 'in_progress'. This filter used to silently return zero projects.
    queryFn: () => listProjects({ status: PROJECT_STATUS.IN_PROGRESS, pageSize: 100 }),
    enabled: show && (movementType === MOVEMENT_TYPE.EXIT || movementReason === MOVEMENT_REASON.PROJECT_CONSUMPTION),
  });

  const projects = projectsData?.data || [];

  // Fetch tasks for selected project
  const { data: tasksData } = useQuery({
    queryKey: ['tasks-for-project', projectId],
    queryFn: () => listTasks({ projectId, pageSize: 100 }),
    enabled: show && !!projectId && movementReason === MOVEMENT_REASON.PROJECT_CONSUMPTION,
  });

  const tasks = tasksData?.data || [];

  // Fetch FIFO preview when quantity changes for exit movements (only in FIFO mode)
  useEffect(() => {
    const fetchPreview = async () => {
      if (
        movementType !== MOVEMENT_TYPE.EXIT ||
        selectionMode !== SELECTION_MODE.FIFO ||
        !quantity ||
        parseFloat(quantity) <= 0
      ) {
        setFifoPreview(null);
        return;
      }

      setLoadingPreview(true);
      try {
        const result = await previewFIFOConsumption(inventoryItem.id, parseFloat(quantity));
        if (!result.error) {
          setFifoPreview(result);
        }
      } catch (e) {
        console.error('FIFO preview error:', e);
      } finally {
        setLoadingPreview(false);
      }
    };

    const debounce = setTimeout(fetchPreview, 300);
    return () => clearTimeout(debounce);
  }, [movementType, quantity, inventoryItem?.id, selectionMode]);

  // Calculate new stock
  const currentStock = inventoryItem?.current_stock || 0;

  // In manual mode, quantity comes from batch selections
  const effectiveQuantity = selectionMode === SELECTION_MODE.MANUAL && showBatchToggle
    ? (batchSelection?.totalQuantity || 0)
    : (parseFloat(quantity) || 0);

  let newStock = currentStock;
  if (movementType === MOVEMENT_TYPE.ENTRY) {
    newStock = currentStock + effectiveQuantity;
  } else if (movementType === MOVEMENT_TYPE.EXIT) {
    newStock = currentStock - effectiveQuantity;
  } else if (movementType === MOVEMENT_TYPE.ADJUSTMENT) {
    newStock = currentStock + effectiveQuantity;
  }

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setMovementType(MOVEMENT_TYPE.ENTRY);
      setMovementReason(MOVEMENT_REASON.PURCHASE);
      setQuantity('');
      setProjectId('');
      setTaskId('');
      setNotes('');
      setError(null);
      setFifoPreview(null);
      setSelectionMode(SELECTION_MODE.FIFO);
      setBatchSelection(null);
      setJustification('');
    }
  }, [show]);

  // Update default reason when movement type changes
  useEffect(() => {
    if (movementType === MOVEMENT_TYPE.ENTRY) {
      setMovementReason(MOVEMENT_REASON.PURCHASE);
    } else if (movementType === MOVEMENT_TYPE.EXIT) {
      setMovementReason(MOVEMENT_REASON.PROJECT_CONSUMPTION);
    } else {
      setMovementReason(MOVEMENT_REASON.INVENTORY_ADJUSTMENT);
    }
    setProjectId('');
    setTaskId('');
    setSelectionMode(SELECTION_MODE.FIFO);
    setBatchSelection(null);
  }, [movementType]);

  // Stable callback for BatchSelector
  const handleBatchSelectionChange = useCallback((selection) => {
    setBatchSelection(selection);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const isManualMode = selectionMode === SELECTION_MODE.MANUAL && showBatchToggle;

    // Validation
    if (isManualMode) {
      if (!batchSelection || batchSelection.totalQuantity <= 0) {
        setError('Debe seleccionar al menos un lote con cantidad mayor a 0');
        return;
      }
    } else {
      if (!quantity || parseFloat(quantity) <= 0) {
        setError('La cantidad debe ser mayor a 0');
        return;
      }
    }

    if (movementReason === MOVEMENT_REASON.PROJECT_CONSUMPTION && !projectId) {
      setError('Debes seleccionar un proyecto para consumo en proyecto');
      return;
    }

    // La base no acepta salidas sin proyecto (`valid_exit_movement`), sea cual
    // sea el motivo — merma, devolución a proveedor o traslado incluidos.
    if (movementType === MOVEMENT_TYPE.EXIT && !projectId) {
      setError('Toda salida de stock debe indicar a qué proyecto se carga.');
      return;
    }

    if (newStock < 0) {
      setError('Stock insuficiente para realizar esta operacion');
      return;
    }

    try {
      setLoading(true);

      // Build notes with reason
      const reasonLabel = MOVEMENT_REASON_LABELS[movementReason] || movementReason;
      const fullNotes = `[${reasonLabel}] ${notes || ''}`.trim();

      if (isManualMode) {
        // Manual batch selection mode
        const result = await consumeFromSpecificBatches(
          inventoryItem.id,
          batchSelection.selections,
          {
            reason: movementReason,
            projectId: projectId || null,
            taskId: taskId || null,
            notes: fullNotes,
            justification: justification || null,
          }
        );

        if (result.error) throw result.error;

        const costMessage = result.totalCost
          ? ` Costo total: ${formatCurrency(result.totalCost)}`
          : '';
        toast.success(`Lotes consumidos exitosamente (seleccion manual).${costMessage}`);
      } else if (movementType === MOVEMENT_TYPE.EXIT && inventoryItem.track_batches !== false) {
        // FIFO consumption for exits when batch tracking is enabled
        const result = await consumeInventoryFIFO(
          inventoryItem.id,
          parseFloat(quantity),
          {
            reason: movementReason,
            projectId: projectId || null,
            taskId: taskId || null,
            notes: fullNotes,
          }
        );

        if (result.error) throw result.error;

        const costMessage = result.totalCost
          ? ` Costo total: ${formatCurrency(result.totalCost)}`
          : '';
        toast.success(`Stock consumido exitosamente (FIFO).${costMessage}`);
      } else {
        // Regular stock movement (entries, adjustments, or items without batch tracking)
        const movementData = {
          inventory_id: inventoryItem.id,
          movement_type: movementType,
          quantity: parseFloat(quantity),
          // `project_id` es la columna que mira el CHECK; `reference_*` es
          // metadato libre. Mandar sólo `reference_id` dejaba `project_id` en
          // NULL y toda salida rebotaba contra `valid_exit_movement`.
          project_id: projectId || null,
          task_id: taskId || null,
          reference_type: projectId ? 'project' : null,
          reference_id: projectId || null,
          notes: fullNotes,
        };

        const result = await recordStockMovement(movementData);

        if (result.error) throw result.error;

        toast.success('Movimiento de stock registrado exitosamente');
      }

      onSuccess();
    } catch (error) {
      console.error('Record movement error:', error);
      setError(error.message || 'Error al registrar el movimiento');
      toast.error('Error al registrar el movimiento');
    } finally {
      setLoading(false);
    }
  };

  const getStockBadgeVariant = () => {
    if (newStock < inventoryItem?.min_stock_level) return 'warning';
    if (newStock === 0) return 'danger';
    return 'success';
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Movimiento de Stock</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Item Info */}
          <div className="mb-3 p-3 bg-light rounded">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h6 className="mb-1">{inventoryItem?.name}</h6>
                {inventoryItem?.sku && (
                  <small className="text-muted">SKU: {inventoryItem.sku}</small>
                )}
              </div>
              <div className="text-end">
                <small className="text-muted d-block">Stock Actual</small>
                <strong className="fs-5">{currentStock}</strong>
                <small className="text-muted ms-1">{inventoryItem?.unit}</small>
              </div>
            </div>
          </div>

          {/* Movement Type */}
          <Form.Group className="mb-3">
            <Form.Label>Tipo de Movimiento</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="Entrada"
                name="movementType"
                id="entry"
                value={MOVEMENT_TYPE.ENTRY}
                checked={movementType === MOVEMENT_TYPE.ENTRY}
                onChange={(e) => setMovementType(e.target.value)}
              />
              <Form.Check
                inline
                type="radio"
                label="Salida"
                name="movementType"
                id="exit"
                value={MOVEMENT_TYPE.EXIT}
                checked={movementType === MOVEMENT_TYPE.EXIT}
                onChange={(e) => setMovementType(e.target.value)}
              />
              <Form.Check
                inline
                type="radio"
                label="Ajuste"
                name="movementType"
                id="adjustment"
                value={MOVEMENT_TYPE.ADJUSTMENT}
                checked={movementType === MOVEMENT_TYPE.ADJUSTMENT}
                onChange={(e) => setMovementType(e.target.value)}
              />
            </div>
          </Form.Group>

          {/* Movement Reason */}
          <Form.Group className="mb-3">
            <Form.Label>Razon del Movimiento</Form.Label>
            <Form.Select
              value={movementReason}
              onChange={(e) => setMovementReason(e.target.value)}
            >
              {availableReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {MOVEMENT_REASON_LABELS[reason]}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Batch Selection Mode Toggle (only for exits with batch tracking) */}
          {showBatchToggle && (
            <Form.Group className="mb-3">
              <Form.Label>Modo de Seleccion de Lote</Form.Label>
              <div>
                <ButtonGroup className="w-100">
                  <ToggleButton
                    id="mode-fifo"
                    type="radio"
                    variant={selectionMode === SELECTION_MODE.FIFO ? 'primary' : 'outline-primary'}
                    name="selectionMode"
                    value={SELECTION_MODE.FIFO}
                    checked={selectionMode === SELECTION_MODE.FIFO}
                    onChange={(e) => {
                      setSelectionMode(e.currentTarget.value);
                      setBatchSelection(null);
                    }}
                    size="sm"
                  >
                    <i className="bi bi-sort-numeric-down me-1"></i>
                    FIFO Automatico
                  </ToggleButton>
                  <ToggleButton
                    id="mode-manual"
                    type="radio"
                    variant={selectionMode === SELECTION_MODE.MANUAL ? 'warning' : 'outline-warning'}
                    name="selectionMode"
                    value={SELECTION_MODE.MANUAL}
                    checked={selectionMode === SELECTION_MODE.MANUAL}
                    onChange={(e) => {
                      setSelectionMode(e.currentTarget.value);
                      setQuantity('');
                      setFifoPreview(null);
                    }}
                    size="sm"
                  >
                    <i className="bi bi-hand-index me-1"></i>
                    Seleccion Manual de Lote
                  </ToggleButton>
                </ButtonGroup>
                {selectionMode === SELECTION_MODE.MANUAL && (
                  <small className="text-muted d-block mt-1">
                    <i className="bi bi-info-circle me-1"></i>
                    Seleccione lotes especificos para trazabilidad ISO 13485
                  </small>
                )}
              </div>
            </Form.Group>
          )}

          {/* Quantity (hidden in manual mode for exits) */}
          {!(selectionMode === SELECTION_MODE.MANUAL && showBatchToggle) && (
            <Form.Group className="mb-3">
              <Form.Label>
                Cantidad{' '}
                {/* Decía "(positivo aumenta, negativo disminuye)" y era
                    mentira por partida doble: la validación de acá rechaza
                    todo valor <= 0, y el CHECK `check_entry_adjustment_positive`
                    de la base tampoco admite un 'adjustment' negativo. Para
                    bajar stock está "Salida", que es el único movimiento que
                    resta — y exige proyecto. */}
                {movementType === MOVEMENT_TYPE.ADJUSTMENT && (
                  <small className="text-muted">
                    (cuánto SUMAR al conteo; para descontar usá "Salida")
                  </small>
                )}
              </Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                required
              />
            </Form.Group>
          )}

          {/* Manual Batch Selector */}
          {selectionMode === SELECTION_MODE.MANUAL && showBatchToggle && (
            <Card className="mb-3 border-warning">
              <Card.Header className="bg-warning bg-opacity-10 py-2">
                <small className="fw-bold">
                  <i className="bi bi-hand-index me-2"></i>
                  Seleccionar Lotes
                </small>
              </Card.Header>
              <Card.Body className="py-2 px-2">
                <BatchSelector
                  inventoryId={inventoryItem?.id}
                  unit={inventoryItem?.unit}
                  onSelectionChange={handleBatchSelectionChange}
                />
              </Card.Body>
            </Card>
          )}

          {/* Justification (only in manual mode) */}
          {selectionMode === SELECTION_MODE.MANUAL && showBatchToggle && (
            <Form.Group className="mb-3">
              <Form.Label>
                Justificacion <small className="text-muted">(por que se salta FIFO)</small>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder="Ej: Se requiere lote especifico por compatibilidad con el equipo medico..."
              />
            </Form.Group>
          )}

          {/* Selector de proyecto.
              Va en TODA salida, no sólo en "consumo en proyecto": el CHECK
              `valid_exit_movement` de la base exige `project_id NOT NULL` en
              cualquier movimiento 'exit'. Sin esto la salida rebota con el
              nombre crudo del constraint y el stock nunca baja. */}
          {(movementType === MOVEMENT_TYPE.EXIT
            || movementReason === MOVEMENT_REASON.PROJECT_CONSUMPTION) && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Proyecto <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={projectId}
                  onChange={(e) => {
                    setProjectId(e.target.value);
                    setTaskId('');
                  }}
                  required
                >
                  <option value="">Seleccionar proyecto...</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.project_number} - {project.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Task Selector (optional) */}
              {projectId && tasks.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label>Tarea <small className="text-muted">(opcional)</small></Form.Label>
                  <Form.Select
                    value={taskId}
                    onChange={(e) => setTaskId(e.target.value)}
                  >
                    <option value="">Sin tarea especifica...</option>
                    {tasks.map((task) => (
                      <option key={task.id} value={task.id}>
                        {task.task_number} - {task.title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
            </>
          )}

          {/* Notes */}
          <Form.Group className="mb-3">
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas adicionales (opcional)..."
            />
          </Form.Group>

          {/* FIFO Preview for Exit movements (only in FIFO mode) */}
          {movementType === MOVEMENT_TYPE.EXIT && selectionMode === SELECTION_MODE.FIFO && quantity && parseFloat(quantity) > 0 && (
            <Card className="mb-3 border-info">
              <Card.Header className="bg-info bg-opacity-10 py-2">
                <small className="fw-bold">
                  <i className="bi bi-layers me-2"></i>
                  Desglose FIFO
                </small>
              </Card.Header>
              <Card.Body className="py-2">
                {loadingPreview ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <small>Calculando...</small>
                  </div>
                ) : fifoPreview && fifoPreview.preview?.length > 0 ? (
                  <>
                    {fifoPreview.preview.map((batch, index) => (
                      <div key={batch.batch_id || index} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                        <div>
                          <small className="fw-medium">
                            Lote {batch.batch_number || `#${index + 1}`}
                          </small>
                          {batch.supplier_name && (
                            <small className="text-muted d-block">
                              {batch.supplier_name}
                            </small>
                          )}
                        </div>
                        {/* admin-only: FIFO cost breakdown */}
                        {isAdmin && (
                          <div className="text-end">
                            <small>
                              {batch.quantity} x {formatCurrency(batch.unit_cost)}
                            </small>
                            <small className="fw-bold d-block">
                              = {formatCurrency(batch.total_cost)}
                            </small>
                          </div>
                        )}
                      </div>
                    ))}
                    {/* admin-only: total cost footer */}
                    {isAdmin && (
                      <div className="d-flex justify-content-between align-items-center pt-2 mt-1 border-top">
                        <strong>Total</strong>
                        <strong className="text-primary">
                          {formatCurrency(fifoPreview.totalCost)}
                        </strong>
                      </div>
                    )}
                    {!fifoPreview.sufficient && (
                      <Alert variant="warning" className="mt-2 mb-0 py-1 px-2">
                        <small>
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Solo hay {fifoPreview.totalAvailable} disponibles
                        </small>
                      </Alert>
                    )}
                  </>
                ) : (
                  <small className="text-muted">
                    Sin lotes disponibles para mostrar
                  </small>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Preview New Stock */}
          {effectiveQuantity > 0 && (
            <div className="p-3 bg-light rounded">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted d-block">Nuevo Stock</small>
                  <strong className="fs-5">
                    {newStock.toFixed(2)}
                  </strong>
                  <small className="text-muted ms-1">{inventoryItem?.unit}</small>
                </div>
                <Badge bg={getStockBadgeVariant()}>
                  {newStock < inventoryItem?.min_stock_level
                    ? 'Bajo Stock'
                    : newStock === 0
                    ? 'Agotado'
                    : 'OK'}
                </Badge>
              </div>
              {newStock < 0 && (
                <Alert variant="danger" className="mt-2 mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Stock insuficiente
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || newStock < 0 || (selectionMode === SELECTION_MODE.MANUAL && showBatchToggle && (!batchSelection || batchSelection.totalQuantity <= 0))}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Registrando...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Registrar Movimiento
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

StockMovementModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  inventoryItem: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default StockMovementModal;
